import { useState } from 'react';
import { supabase } from '../lib/supabase';

interface TenantData {
  hospitalName: string;
  subdomain: string;
  customDomain?: string;
  plan: 'starter' | 'professional' | 'enterprise';
  primaryColor: string;
  secondaryColor: string;
  adminEmail: string;
  adminName: string;
}

export const useTenantSetup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTenant = async (data: TenantData) => {
    setLoading(true);
    setError(null);

    try {
      // Critical validation: ensure name is never null/undefined/empty
      if (!data.hospitalName || typeof data.hospitalName !== 'string' || data.hospitalName.trim() === '') {
        throw new Error('Hospital name cannot be null, undefined, or empty');
      }
      
      if (!data.subdomain || typeof data.subdomain !== 'string' || data.subdomain.trim() === '') {
        throw new Error('Subdomain cannot be null, undefined, or empty');
      }

      // 1. Create tenant record
      const tenantRecord: Database['public']['Tables']['tenants']['Insert'] = {
        id: `${data.subdomain}-hospital`,
        name: data.hospitalName,
        subdomain: data.subdomain,
        custom_domain: data.customDomain || null,
        plan: data.plan,
        status: 'active',
        branding: {
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          hospitalName: data.hospitalName,
          logo: null
        },
        features: {
          aiPredictions: true,
          automatedReminders: true,
          analytics: true,
          apiAccess: data.plan !== 'starter',
          customIntegrations: data.plan === 'enterprise',
          whiteLabeling: data.plan === 'enterprise'
        },
        limits: {
          maxUsers: data.plan === 'starter' ? 5 : data.plan === 'professional' ? 25 : 999,
          maxAppointments: data.plan === 'starter' ? 1000 : data.plan === 'professional' ? 10000 : 999999,
          maxPredictions: data.plan === 'starter' ? 100 : data.plan === 'professional' ? 1000 : 999999,
          dataRetentionDays: 365
        },
        billing: {
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          monthlyPrice: data.plan === 'starter' ? 99 : data.plan === 'professional' ? 299 : 999
        }
      };

      // Final validation before database operation
      if (!tenantRecord.name || typeof tenantRecord.name !== 'string' || tenantRecord.name.trim() === '') {
        console.error('Final tenant validation failed:', { tenantRecord });
        throw new Error('Tenant name validation failed before database operation');
      }

      console.log('Creating tenant with validated data:', { name: tenantRecord.name, id: tenantRecord.id });

      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .upsert(tenantRecord, { onConflict: 'id' })
        .select()
        .single();

      if (tenantError) throw tenantError;

      // 2. Create default reminder rules
      const reminderRules = [
        // High Risk
        { risk_level: 'high', timing: '72h', channels: ['email', 'sms'], template: 'High risk 72h reminder', enabled: true },
        { risk_level: 'high', timing: '24h', channels: ['sms'], template: 'High risk 24h reminder', enabled: true },
        { risk_level: 'high', timing: '2h', channels: ['sms', 'voice'], template: 'High risk 2h reminder', enabled: true },
        
        // Medium Risk
        { risk_level: 'medium', timing: '48h', channels: ['email'], template: 'Medium risk 48h reminder', enabled: true },
        { risk_level: 'medium', timing: '4h', channels: ['sms'], template: 'Medium risk 4h reminder', enabled: true },
        
        // Low Risk
        { risk_level: 'low', timing: '24h', channels: ['email'], template: 'Low risk 24h reminder', enabled: true }
      ];

      const { error: rulesError } = await supabase
        .from('reminder_rules')
        .upsert(reminderRules, { onConflict: 'id' });

      if (rulesError) throw rulesError;

      return {
        success: true,
        tenant,
        message: `Tenant created successfully! URL: https://${data.subdomain}.attendai.io`
      };

    } catch (err: any) {
      console.error('Error in createTenant:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  const checkTenantExists = async (subdomain: string) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, subdomain')
        .eq('subdomain', subdomain)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    } catch (err) {
      console.error('Error checking tenant:', err);
      return false;
    }
  };

  const getTenantBySubdomain = async (subdomain: string) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('subdomain', subdomain)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching tenant:', err);
      return null;
    }
  };

  return {
    createTenant,
    checkTenantExists,
    getTenantBySubdomain,
    loading,
    error
  };
};